import { useEffect, useState, useRef, forwardRef, useCallback, useMemo } from "react";
import { useImperativeHandle } from "react";
import styles from "./wrap.module.scss";

const defaultFadeHeight = { before: 50, after: 90, fadeStart: "100%" };

// Props
interface Props {
  fullHeight?: boolean;
  className?: string;
  headerComponent?: React.ReactNode;
  contentsComponent?: {
    contents: React.ReactNode;
    actions?: React.ReactNode;
    extra?: React.ReactNode;
  };
  viewportResize?: boolean;
  maxWidth?: string;
  actionsWrapProps?: {
    fade?: boolean;
    fadeHeight?: { before?: number; after?: number; fadeStart?: string };
    style?: React.CSSProperties;
  };
  contentsWrapProps?: {
    style?: React.CSSProperties;
  };
}

/**
 * 랩 (페이지 단위)
 *
 * @param fullHeight 전체 높이 설정 여부
 * @param className 클래스명
 * @param headerComponent 헤더 컴포넌트
 * @param contentsComponent 컨텐츠 컴포넌트
 * @param accessType 접근 유형 (visitor: 방문자, member: 회원, all: 모두)
 * @param viewportResize 뷰포트 리사이즈 시 스크롤 이동 여부
 * @param maxWidth 최대 너비
 */

const Wrap = forwardRef(
  (
    {
      fullHeight = true,
      className = "",
      headerComponent,
      contentsComponent,
      viewportResize = false, // true
      maxWidth,
      actionsWrapProps = {
        fade: true,
        fadeHeight: defaultFadeHeight,
        style: {},
      },
      contentsWrapProps = {
        style: {},
      },
    }: Props,
    ref,
  ) => {
    // 높이 상태
    const [defaultHeight, setDefaultHeight] = useState(0);
    const [height, setHeight] = useState(0);

    // 로딩 상태
    const [loading, setLoading] = useState(false);

    // 요소 참조
    const wrapRef = useRef<HTMLDivElement>(null);
    const contentsRef = useRef<HTMLDivElement>(null);
    const actionsRef = useRef<HTMLDivElement>(null);
    // 전달받은 ref가 있으면 그 ref에 innerRef를 연결
    useImperativeHandle(ref, () => ({
      wrap: wrapRef.current,
      contents: contentsRef?.current,
      actions: actionsRef?.current,
      calcProperties,
    }));

    // 상위 요소 목록
    const parentList = useRef<HTMLElement[]>();

    // documnet active element 참조
    const documentActiveElement = useRef<HTMLElement | null>(null);

    // 뷰포트 리사이즈 핸들러
    const handleVisualViewPortResize = useCallback(() => {
      if (!wrapRef.current) return;
      const scrollElement = wrapRef.current;
      documentActiveElement.current = document?.activeElement as HTMLElement;
      const currentVisualViewport = Number(window.visualViewport?.height);

      // 뷰포트 높이가 변했을 때, 스크롤 높이를 조정
      if (scrollElement) {
        scrollElement.style.height = `${currentVisualViewport}px`;
        if (Math.abs(defaultHeight - currentVisualViewport) > 3) {
          wrapRef.current.classList.add("sharedui-window-height-resize");
        } else {
          wrapRef.current.classList.remove("sharedui-window-height-resize");
        }
        window.scrollTo(0, 0);
        // input, textarea 요소에 포커스 시, 해당 요소로 스크롤 이동
        setTimeout(() => {
          window.scrollTo(0, 0);
          if (!documentActiveElement.current) return;
          if (documentActiveElement.current !== document.body) {
            if (
              documentActiveElement.current.tagName === "INPUT" ||
              documentActiveElement.current.tagName === "TEXTAREA"
            ) {
              documentActiveElement.current.scrollIntoView({
                behavior: "smooth",
                block: "end",
              });
            }
            documentActiveElement.current = null;
          }
        }, 200);
      }
    }, [defaultHeight]);

    // 초기 높이 설정
    useEffect(() => {
      if (wrapRef?.current) {
        if (typeof window === "undefined") return;
        setDefaultHeight(window.outerHeight);
        setHeight(window.outerHeight);
        parentList.current = findParentsElement(wrapRef.current);
      }
    }, [wrapRef?.current]);

    // fullHeight 값에 따라 스크롤 막기 / 요소 높이 지정 등 처리
    useEffect(() => {
      if (typeof window === "undefined") return;
      if (!parentList?.current) return;
      if (parentList.current.length === 0) return;
      if (!window?.visualViewport) return;

      // fullHeight가 true일 때, 부모 요소의 높이를 window.outerHeight로 설정
      if (fullHeight) {
        // 부모 요소의 높이를 window.outerHeight로 설정, 스크롤 막기
        parentList.current.forEach((parent) => {
          parent.style.height = window.outerHeight + "px";
          parent.style.overflow = "hidden";
          parent.style.touchAction = "none";
          parent.style.overscrollBehavior = "none";
        });
        if (viewportResize) {
          window.visualViewport.addEventListener("resize", handleVisualViewPortResize);
          // window.visualViewport.addEventListener("scroll", handleVisualViewPortResize);
        }
        return () => {
          if (typeof window === "undefined") return;
          if (!parentList?.current) return;
          if (parentList.current.length === 0) return;
          if (!window?.visualViewport) return;
          parentList.current.forEach((parent) => {
            parent.style.height = "";
            parent.style.overflow = "";
            parent.style.touchAction = "";
            parent.style.overscrollBehavior = "";
          });

          if (viewportResize) {
            window.visualViewport.removeEventListener("resize", handleVisualViewPortResize);
            // window.visualViewport.removeEventListener("scroll", handleVisualViewPortResize);
          }
        };
        // fullHeight가 false일 때, 부모 요소의 최소 높이를 window.outerHeight로 설정
      } else {
        parentList.current.forEach((parent) => {
          parent.style.minHeight = window.outerHeight + "px";
        });

        const resize = () => {
          setHeight(window.outerHeight);
        };

        if (viewportResize) {
          if (typeof window === "undefined") return;
          if (!document?.body || !document?.documentElement) return;
          if (!window?.visualViewport) return;
          window.visualViewport.addEventListener("resize", resize);
          // window.visualViewport.addEventListener("scroll", resize);
        }

        return () => {
          if (viewportResize) {
            if (typeof window === "undefined") return;
            if (!document?.body || !document?.documentElement) return;
            if (!window?.visualViewport) return;
            window.visualViewport.addEventListener("resize", resize);
            // window.visualViewport.addEventListener("scroll", resize);
          }
        };
      }
    }, [fullHeight, parentList?.current]);

    // 로딩 상태 변경
    useEffect(() => {
      setTimeout(() => {
        setLoading(true);
      }, 150);
    }, []);

    // 액션 영역 fade 효과 계산 + 컨텐츠 영역 패딩 바텀 계산
    const calcProperties = () => {
      if (!contentsRef?.current) {
        return;
      }

      const fadeValue =
        { ...defaultFadeHeight, ...actionsWrapProps?.fadeHeight } ?? defaultFadeHeight;

      let actionsHeight = 0;
      let actionsBeforeHeight = 0;
      let actionsAfterHeight = 0;
      // 액션 버튼 영역에 가상요소를 추가하여 fade 효과를 줬습니다.
      // 해당 요소의 높이를 계산하여 properties에 추가해주었습니다. (before: 액션 높이 대비 5/8, after: 액션 높이 대비 9/8)
      // 자연스럽게 컨텐츠 요소를 가리게 하기 위해 컨텐츠 요소를 스타일값으로 조정하였습니다. 아래는 해당 조절을 위한 코드입니다.
      let customContentsPaddingBottom = contentsWrapProps?.style?.paddingBottom;
      if (typeof customContentsPaddingBottom === "number") {
        customContentsPaddingBottom = `${customContentsPaddingBottom}px`;
      }

      if (actionsRef?.current) {
        actionsHeight = actionsRef.current.clientHeight;
        if (actionsHeight) {
          actionsBeforeHeight = actionsWrapProps?.fade
            ? (fadeValue?.before ?? Math.min((actionsHeight / 8) * 5, 50))
            : 0;
          actionsRef.current.style.setProperty("--fade-before-height", `${actionsBeforeHeight}px`); // 버튼 영역의 5/8 높이
          actionsAfterHeight = actionsWrapProps?.fade
            ? (fadeValue?.after ?? (actionsHeight / 8) * 9)
            : 0;
          actionsRef.current.style.setProperty("--fade-after-height", `${actionsAfterHeight}px`); // 버튼 영역의 9/8 높이

          actionsRef.current.style.setProperty(
            "--fade-start",
            `${fadeValue?.fadeStart ?? "100%"}`, // fade 효과 시작 지점
          );
          contentsRef.current.style.setProperty(
            "--actions-height",
            `${actionsRef?.current ? Math.max(actionsBeforeHeight + actionsAfterHeight, actionsHeight) : 0}px`, // 버튼 영역의 5/8 + 9/8 높이 (컨텐츠가 가려지는 높이)
          );
        } else {
          contentsRef.current.style.setProperty("--actions-height", "0px");
        }
      }
      contentsRef.current.style.setProperty(
        "--padding-bottom",
        `${actionsRef?.current ? (customContentsPaddingBottom ?? `0px`) : "0px"}`, // 컨텐츠의 추가 paddingBottom
      );
    };

    // ios에서 viewport가 줄어들었을때, contentsRef 영역을 스크롤하면 body까지 스크롤되는 현상 방지
    const handleTouchMove = useCallback((e: TouchEvent) => {
      const contentsTarget = contentsRef?.current;
      if (!contentsTarget) return;
      const isScrollable = contentsTarget?.scrollHeight > contentsTarget?.clientHeight;
      if (isScrollable) {
        e.stopPropagation();
      } else {
        const target = e.target as HTMLElement;
        if (target === contentsTarget) {
          e.preventDefault();
        }
      }
    }, []);

    // ios에서 contentsRef 영역을 스크롤하면 body까지 스크롤되는 현상 방지
    useEffect(() => {
      document.documentElement.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      return () => {
        document.documentElement.removeEventListener("touchmove", handleTouchMove);
      };
    }, []);

    // 이전 계산된 프로퍼티
    const prevCalcProperties = useRef({
      contentsRef: null,
      actionsRef: null,
      actionsWrapProps: null,
      contentsWrapProps: null,
    });

    // 프로퍼티 변경 시, 계산
    useEffect(() => {
      const isChangeProps = (
        prevContentsRef: typeof contentsRef.current | null,
        prevActionsRef: typeof actionsRef.current | null,
        prevActionsWrapProps: typeof actionsWrapProps | null,
        prevContentsWrapProps: typeof contentsWrapProps | null,
      ) => {
        return (
          prevContentsRef !== contentsRef?.current ||
          prevActionsRef !== actionsRef?.current ||
          JSON.stringify(prevActionsWrapProps) !== JSON.stringify(actionsWrapProps) ||
          JSON.stringify(prevContentsWrapProps) !== JSON.stringify(contentsWrapProps)
        );
      };

      if (
        isChangeProps(
          prevCalcProperties.current.contentsRef,
          prevCalcProperties.current.actionsRef,
          prevCalcProperties.current.actionsWrapProps,
          prevCalcProperties.current.contentsWrapProps,
        )
      ) {
        calcProperties();
      }

      prevCalcProperties.current = {
        contentsRef: contentsRef?.current,
        actionsRef: actionsRef?.current,
        actionsWrapProps,
        contentsWrapProps,
      };
    }, [actionsWrapProps, contentsWrapProps, contentsRef?.current, actionsRef?.current]);

    return (
      <div
        ref={wrapRef}
        className={`${fullHeight ? styles["full-height-wrap"] : styles["auto-height-wrap"]} ${
          className ? className : ""
        }`}
        style={{
          maxWidth: maxWidth ? maxWidth : "",
          left: maxWidth ? "50%" : "",
          transform: maxWidth ? "translateX(-50%) translateZ(0)" : "",
          height: fullHeight
            ? viewportResize
              ? height + "px"
              : typeof window === "undefined"
                ? "100%"
                : window.outerHeight + "px"
            : "",
          minHeight: !fullHeight ? height + "px" : "",
          opacity: loading ? 1 : 0,
        }}
      >
        {headerComponent && headerComponent}
        {contentsComponent?.contents && (
          <div
            ref={contentsRef}
            className={`${styles["contents-wrap"]}`}
            style={{
              ...contentsWrapProps.style,
              paddingBottom: "",
              marginBottom: "",
            }}
          >
            {contentsComponent.contents}
          </div>
        )}
        {contentsComponent.actions && (
          <div
            className={styles["actions-wrap"]}
            ref={actionsRef}
            style={{ ...actionsWrapProps.style }}
          >
            {contentsComponent.actions}
          </div>
        )}
        {contentsComponent?.extra && contentsComponent.extra}
      </div>
    );
  },
);
Wrap.displayName = "Wrap";

export default Wrap;

const findParentsElement = (element: HTMLElement) => {
  const parentsElement = [];
  let parent = element.parentElement;
  while (parent) {
    parentsElement.push(parent);
    parent = parent.parentElement;
  }
  return parentsElement;
};
